import { GENERATED_ANIMATED_SHEETS } from './animated-catalog.generated.js';

/**
 * Catalog of looping pictures for animated objects (ADR-0040), from the LimeZu "Animated objects"
 * pack (32x32, `tile_assets/05_Animated_objects/32x32/spritesheets`). Each sheet is one row of
 * equally wide frames; the PNG is served by the client from `public/animated/<sheet>.png`, and
 * only when a map uses it. Frame rates stay inside the style guide's 2 to 12 fps (§5.1).
 */
export interface AnimatedSheet {
  /** Picture size in tiles. */
  readonly width: number;
  readonly height: number;
  readonly frames: number;
  readonly fps: number;
}

const CURATED_SHEETS = {
  coffee: { width: 1, height: 2, frames: 6, fps: 6 },
  candle: { width: 1, height: 2, frames: 3, fps: 6 },
  wall_candle: { width: 1, height: 2, frames: 3, fps: 6 },
  cuckoo_clock: { width: 1, height: 2, frames: 10, fps: 5 },
  pendulum_clock: { width: 1, height: 3, frames: 4, fps: 4 },
  fishtank_orange: { width: 2, height: 2, frames: 8, fps: 5 },
  old_tv: { width: 2, height: 2, frames: 6, fps: 8 },
  hospital_screen_color: { width: 2, height: 2, frames: 5, fps: 4 },
  receptionist: { width: 1, height: 2, frames: 7, fps: 4 },
  control_room_server: { width: 1, height: 3, frames: 3, fps: 4 },
  door_2_locked: { width: 1, height: 2, frames: 4, fps: 4 },
  security_camera_wall_left: { width: 1, height: 1, frames: 10, fps: 4 },
  fishtank_red: { width: 2, height: 2, frames: 8, fps: 5 },
  kitchen_microwave: { width: 1, height: 2, frames: 6, fps: 5 },
  toaster: { width: 1, height: 2, frames: 11, fps: 6 },
} as const satisfies Record<string, AnimatedSheet>;

/**
 * The curated sheets above plus every other loop in the pack, generated by
 * `scripts/make-animated-catalog.py`. A curated entry wins over a generated one with the same key.
 */
export const ANIMATED_SHEETS: Readonly<Record<string, AnimatedSheet>> = {
  ...GENERATED_ANIMATED_SHEETS,
  ...CURATED_SHEETS,
};

export const ANIMATED_SHEET_KEYS: readonly string[] = Object.keys(ANIMATED_SHEETS).sort();

export function animatedSheet(key: string): AnimatedSheet | undefined {
  return Object.hasOwn(ANIMATED_SHEETS, key) ? ANIMATED_SHEETS[key] : undefined;
}

/** Art a door uses when its map object names none (`door_sheet`, ADR-0043). */
export const DEFAULT_DOOR_SHEET = 'door_1';

/**
 * Sheets offered as door art: the pack's swinging, sliding and lift doors. Trapdoors and the
 * lock-shake loops (`*_locked`) are left out.
 */
export const DOOR_SHEET_KEYS: readonly string[] = ANIMATED_SHEET_KEYS.filter(
  (key) => /door/.test(key) && !key.includes('trapdoor') && !key.endsWith('_locked'),
);

/**
 * The frames a door sheet shows closed and open (ADR-0043). Short sheets run closed to open;
 * longer ones are a full open-and-close cycle, so the open door is the middle frame. Vertical
 * doors follow the same rule: frame 0 is the door edge-on, lying in the wall, which is closed.
 */
export function doorFrames(sheet: string): { readonly closed: number; readonly open: number } {
  const frames = animatedSheet(sheet)?.frames ?? 1;
  return { closed: 0, open: frames <= 5 ? frames - 1 : Math.floor(frames / 2) };
}

/** Object types drawn as an animated door (ADR-0043). */
export const DOOR_TYPES: readonly string[] = ['toggle_door', 'coop_door', 'pin_door'];

/** The sheet a door object uses: its `door_sheet` property if it names a known sheet, else the default. */
export function doorSheetOf(
  properties: Readonly<Record<string, string | number | boolean>>,
): string {
  const value = properties.door_sheet;
  return typeof value === 'string' && animatedSheet(value) ? value : DEFAULT_DOOR_SHEET;
}
